import { useRef } from 'react';

interface FormattingToolbarProps {
  onInsertImage: (url: string) => void;
}

/**
 * Rich text formatting toolbar for the visual editor.
 * Uses document.execCommand for inline formatting of the contentEditable preview.
 */
export function FormattingToolbar({ onInsertImage }: FormattingToolbarProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const exec = (command: string, value?: string) => {
    document.execCommand(command, false, value);
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target?.result as string;
      onInsertImage(dataUrl);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleImageUrl = () => {
    const url = window.prompt('Enter image URL:');
    if (url) onInsertImage(url);
  };

  return (
    <div className="flex flex-wrap items-center gap-0.5 border-b border-gray-200 bg-white px-3 py-1.5 dark:border-gray-700 dark:bg-gray-800">
      {/* Text Formatting */}
      <ToolbarGroup label="Format">
        <ToolBtn onClick={() => exec('bold')} title="Bold" label="B" className="font-bold" />
        <ToolBtn onClick={() => exec('italic')} title="Italic" label="I" className="italic" />
        <ToolBtn
          onClick={() => exec('underline')}
          title="Underline"
          label="U"
          className="underline"
        />
        <ToolBtn
          onClick={() => exec('strikeThrough')}
          title="Strikethrough"
          label="S"
          className="line-through"
        />
      </ToolbarGroup>

      <Separator />

      {/* Font Size */}
      <ToolbarGroup label="Size">
        <select
          onChange={(e) => exec('fontSize', e.target.value)}
          defaultValue="3"
          className="h-7 rounded border border-gray-300 bg-white px-1 text-xs dark:border-gray-600 dark:bg-gray-700 dark:text-white"
          title="Font size"
          aria-label="Font size"
        >
          <option value="1">8px</option>
          <option value="2">10px</option>
          <option value="3">12px</option>
          <option value="4">14px</option>
          <option value="5">18px</option>
          <option value="6">24px</option>
          <option value="7">36px</option>
        </select>
      </ToolbarGroup>

      <Separator />

      {/* Text Color */}
      <ToolbarGroup label="Color">
        <label className="relative cursor-pointer" title="Text color">
          <span className="flex h-7 w-7 items-center justify-center rounded text-xs hover:bg-gray-100 dark:hover:bg-gray-700">
            A
          </span>
          <input
            type="color"
            className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
            onChange={(e) => exec('foreColor', e.target.value)}
            aria-label="Text color"
          />
        </label>
        <label className="relative cursor-pointer" title="Background color">
          <span className="flex h-7 w-7 items-center justify-center rounded bg-yellow-200 text-xs hover:bg-yellow-300">
            A
          </span>
          <input
            type="color"
            className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
            onChange={(e) => exec('hiliteColor', e.target.value)}
            aria-label="Highlight color"
          />
        </label>
      </ToolbarGroup>

      <Separator />

      {/* Alignment */}
      <ToolbarGroup label="Align">
        <ToolBtn onClick={() => exec('justifyLeft')} title="Align left" label="≡" />
        <ToolBtn
          onClick={() => exec('justifyCenter')}
          title="Align center"
          label="≡"
          className="text-center"
        />
        <ToolBtn
          onClick={() => exec('justifyRight')}
          title="Align right"
          label="≡"
          className="text-right"
        />
      </ToolbarGroup>

      <Separator />

      {/* Lists */}
      <ToolbarGroup label="List">
        <ToolBtn onClick={() => exec('insertUnorderedList')} title="Bullet list" label="•" />
        <ToolBtn onClick={() => exec('insertOrderedList')} title="Numbered list" label="1." />
      </ToolbarGroup>

      <Separator />

      {/* Image */}
      <ToolbarGroup label="Insert">
        <ToolBtn
          onClick={() => fileInputRef.current?.click()}
          title="Upload image/logo"
          label="🖼"
        />
        <ToolBtn onClick={handleImageUrl} title="Image from URL" label="🔗" />
      </ToolbarGroup>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleImageUpload}
        className="hidden"
        aria-label="Upload image"
      />
    </div>
  );
}

function ToolBtn({
  onClick,
  title,
  label,
  className,
}: {
  onClick: () => void;
  title: string;
  label: string;
  className?: string;
}) {
  return (
    <button
      type="button"
      // Keep focus (and the text selection) in the canvas while clicking.
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      title={title}
      className={`flex h-7 w-7 items-center justify-center rounded text-xs text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700 ${className || ''}`}
    >
      {label}
    </button>
  );
}

function ToolbarGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-0.5" role="group" aria-label={label}>
      {children}
    </div>
  );
}

function Separator() {
  return <div className="mx-1 h-5 w-px bg-gray-300 dark:bg-gray-600" />;
}
